/**
 * DashboardInsights — новый Дашборд (роль admin/superadmin).
 *
 * Экран роли «с одного взгляда»: приветствие + тайлы обзора + ИИ-советник
 * (AdminAdvisor: метрики, ИИ-строка, карточки-действия) + онбординг для cold.
 * Один фетч GET /api/tasks/advisor на странице, данные прокидываются в advisor.
 * Воркер сюда не попадает (роутинг ведёт его на /tasks).
 *
 * Каркас расширяемый: ниже advisor'а — место под будущие блоки инсайтов
 * (график выручки, загрузка объектов и т.д.).
 */
import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useAuth } from '../hooks/useAuth';
import { usePerm } from '../hooks/usePerm';
import { usePlan } from '../hooks/usePlan';
import { useFabState } from '../hooks/useFabState';
import { track } from '../analytics';
import { api } from '../api/client';
import AdminAdvisor from '../components/AdminAdvisor';
import AdminIntro from '../components/AdminIntro';
import EquipmentQueue from '../components/EquipmentQueue';
import OnboardingChecklist from '../components/OnboardingChecklist';
import FabSpeedDial from '../components/FabSpeedDial';
import ExpenseFab from '../components/ExpenseFab';

// Модалки открываются только по клику — не тащим их в критический путь
// первого экрана (TaskModal ~1000 строк со всем композером).
const TaskModal       = lazy(() => import('../components/TaskModal'));
const IncomeListModal = lazy(() => import('../components/IncomeListModal'));

function Tile({ label, value, accent, onClick }) {
  return (
    <button onClick={onClick} className="dash-tile" style={{
      textAlign: 'center', cursor: 'pointer', border: '1px solid var(--border)',
      background: 'var(--bg2)', borderRadius: 'var(--radius-lg)', padding: '12px 14px',
      minWidth: 0, transition: 'border-color var(--transition), background var(--transition)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginTop: 4, color: accent || 'var(--text)' }}>{value}</div>
    </button>
  );
}

export default function DashboardInsights() {
  const { user } = useAuth();
  const { hasFeature } = usePlan();
  const { hidden } = useFabState();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'en' ? enUS : ru;

  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);

  // Действия FAB + модал начислений (ждущих оплаты) + AMC overdue.
  const [showAwaiting, setShowAwaiting] = useState(false);
  const [showAmcOverdue, setShowAmcOverdue] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showExpenseFab, setShowExpenseFab] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const canExpenses = !user?.is_demo && hasFeature('expenses');

  // Персональный онбординг админа (per-user). user.admin_onboarded приходит из /me.
  // introDismissed — закрыл «Понятно» в этой сессии; introReopened — вернул через
  // recovery-плашку (показываем снова, не трогая серверный флаг).
  // Денежные показатели дашборда — по флагу, а не по роли
  const { can } = usePerm();
  const isAdmin = can('dashboard.see_money');
  const [introDismissed, setIntroDismissed] = useState(false);
  const [introReopened, setIntroReopened]   = useState(false);
  // Action-point интро: фото + телефон в профиле. Пока не выполнен — даём вернуть гайд.
  const profileDone = !!user?.phone && !!user?.avatar_thumb_url;

  // Переход на Работы с включённым фильтром «Без начислений» (выполнено, не выставлено).
  const goUninvoiced = useCallback(
    () => navigate('/tasks', { state: { returnFilter: 'done', uninvoicedOnly: true } }),
    [navigate]
  );

  // Переход на Работы с фильтром «Не просмотрено» (назначено, но исполнитель не открыл).
  const goUnviewed = useCallback(
    () => navigate('/tasks', { state: { returnFilter: 'all', unviewedOnly: true } }),
    [navigate]
  );

  // Чеклист онбординга. Скрытие, «Уже сделал» и состав шагов живут на сервере
  // (per-user), а не в localStorage: иначе человек, зашедший с телефона, получал
  // блок заново, а мы не видели в аналитике, на каком шаге застревают.
  // Здесь остаётся только ключ ремаунта для ссылки «Вернуть чеклист».
  const [checklistNonce, setChecklistNonce] = useState(0);
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const [checklistAllDone, setChecklistAllDone] = useState(false);

  const handleChecklistShow = async () => {
    try { await api.post('/onboarding/dismiss', { restore: true }); } catch { /* покажем при след. загрузке */ }
    setChecklistDismissed(false);
    setChecklistNonce(n => n + 1);
  };
  const handleChecklistAllDone = () => {
    setChecklistAllDone(true);
    // НЕ авто-скрываем — пользователь видит 🎉 и сам нажимает «Закрыть»
  };
  useEffect(() => {
    let alive = true;
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    setLineLoading(false);
    // Фаза 1: мгновенный детерминированный контент (обзор/метрики/карточки).
    api.get(`/tasks/advisor?lang=${lang}`)
      .then(r => {
        if (!alive) return;
        setData(r); setLoaded(true);
        // Фаза 2: ИИ-строка догружается отдельно (может занять ~1с) —
        // под анимацию «звёздной пыли», чтобы лаг не ощущался.
        if (r && r.state === 'live' && !r.line) {
          setLineLoading(true);
          api.get(`/tasks/advisor/line?lang=${lang}`)
            .then(res => { if (alive) setData(d => ({ ...d, line: res.line })); })
            .catch(() => {})
            .finally(() => { if (alive) setLineLoading(false); });
        }
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [i18n.language, reloadKey]);

  const firstName = (user?.name || '').split(' ')[0];
  const counts = data?.signals?.counts;
  const showOverview = loaded && counts && data?.state !== 'cold';

  // Гейт интро: зрелая орга (не cold — у cold онбординг закрывает OnboardingChecklist).
  const introEligible = isAdmin && !user?.is_demo && user?.setup_completed
    && loaded && !!data?.state && data.state !== 'cold';
  // На экране сейчас: ещё не закрыто И (не помечено seen ИЛИ возвращено вручную).
  const introOnScreen = introEligible && !introDismissed && (!user?.admin_onboarded || introReopened);
  // Recovery-плашка: интро закрыто, но Action (фото+телефон) не выполнен.
  const showIntroRecovery = introEligible && !introOnScreen && !profileDone;

  return (
    <div style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}>
      <div className="page-header page-header--scroll">
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            {firstName ? t('dashboardNew.greetingName', { name: firstName }) : t('dashboardNew.greeting')}
          </h2>
          <div className="subtitle">{format(new Date(), 'EEEE, d MMMM', { locale: dateLocale })}</div>
        </div>
      </div>

      <div style={{ padding: '12px 16px 0' }}>
        {/* Персональное welcome-интро: новый админ в зрелой орге (state !== cold —
            у cold-орги онбординг закрывает OnboardingChecklist, дубль не нужен) */}
        {introOnScreen && (
          <AdminIntro
            name={firstName}
            profileDone={profileDone}
            onProfile={() => navigate('/profile', { state: { tab: 'personal' } })}
            onDone={() => setIntroDismissed(true)}
          />
        )}

        {/* Recovery: вернуть гайд, пока Action (фото+телефон) не выполнен */}
        {showIntroRecovery && (
          <div
            data-ph-id="admin-intro-recovery"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, padding: '12px 16px', marginBottom: 16,
              background: 'var(--bg2, #f5f5f7)', border: '1px solid var(--border, #e0e0e8)',
              borderRadius: 12,
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              ✨ {t('adminIntro.recoveryText')}
            </span>
            <button
              onClick={() => { setIntroReopened(true); setIntroDismissed(false); track('admin_intro_reopened'); }}
              data-ph-id="admin-intro-reopen"
              style={{
                background: 'none', border: 'none', color: 'var(--accent)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '2px 0', whiteSpace: 'nowrap',
              }}
            >
              {t('adminIntro.recoveryCta')} →
            </button>
          </div>
        )}

        {showOverview && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', margin: '0 2px 10px' }}>
              {t('dashboardNew.overview.title')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 18 }}>
              <Tile label={t('dashboardNew.overview.properties')} value={counts.properties} onClick={() => navigate('/properties')} />
              <Tile label={t('dashboardNew.overview.tasks')} value={counts.tasks} onClick={() => navigate('/tasks', { state: { returnFilter: 'all' } })} />
              <Tile label={t('dashboardNew.overview.done')} value={counts.tasks_done} accent="var(--green, #0f6e56)" onClick={() => navigate('/tasks', { state: { returnFilter: 'done' } })} />
            </div>
          </>
        )}

        {/* Очередь подтверждения оборудования — сама прячется, когда пуста. */}
        <EquipmentQueue />

        {/* ИИ-советник: warming/live рисует сам, cold — молчит (ниже онбординг). */}
        <AdminAdvisor
          controlled
          data={data}
          lineLoading={lineLoading}
          onFilterDone={() => navigate('/tasks', { state: { returnFilter: 'done' } })}
          onFilterUninvoiced={goUninvoiced}
          onFilterUnviewed={goUnviewed}
          onOpenAwaiting={() => setShowAwaiting(true)}
          onOpenAmcOverdue={() => setShowAmcOverdue(true)}
          onChanged={() => setReloadKey(k => k + 1)}
        />

        {/* Чеклист онбординга — сам показывает 🎉 когда всё готово; Закрыть → dismissed.
            Состав шагов приходит с сервера уже по роли, стране и модулям. */}
        {!user?.is_demo && user?.setup_completed && !checklistDismissed && (
          <OnboardingChecklist
            key={checklistNonce}
            onTaskCreate={() => navigate('/tasks')}
            onClientAdd={() => navigate('/contacts')}
            onPropertyAdd={() => navigate('/properties')}
            onWorkerInvite={() => navigate('/settings')}
            onLogoUpload={() => navigate('/settings')}
            onDismissed={() => setChecklistDismissed(true)}
            onAllDone={handleChecklistAllDone}
          />
        )}

        {/* Recovery — видна когда скрыт, но ещё не завершён; прячется если всё done */}
        {!user?.is_demo && user?.setup_completed && checklistDismissed && !checklistAllDone && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px',
              background: 'var(--bg2, #f5f5f7)',
              border: '1px solid var(--border, #e0e0e8)',
              borderRadius: 12,
              marginBottom: 16,
            }}
            data-ph-id="onboarding-checklist-recovery"
          >
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>
              📋 {t('onboarding.checklist.title')}
            </span>
            <button
              onClick={handleChecklistShow}
              style={{
                background: 'none', border: 'none',
                color: 'var(--accent)', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, padding: '2px 0',
              }}
              data-ph-id="onboarding-checklist-show"
            >
              {t('onboarding.checklist.showChecklist')} →
            </button>
          </div>
        )}
      </div>

      {/* FAB-меню действий (как на Работах): новая работа + расход */}
      <FabSpeedDial
        hidden={hidden || showAwaiting}
        actions={[
          {
            label: t('tasks.newTask'),
            color: '#2563EB',
            onClick: () => { setShowNewTask(true); track('fab_clicked', { screen: 'dashboard' }); },
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
          },
          ...(canExpenses ? [{
            label: t('expenses.newExpense'),
            color: '#0d7a4e',
            onClick: () => { setShowExpenseFab(true); track('expense_fab_clicked', { screen: 'dashboard' }); },
            icon: <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>$</span>,
          }] : []),
        ]}
      />

      {showNewTask && (
        <Suspense fallback={null}>
          <TaskModal
            onClose={() => setShowNewTask(false)}
            onSave={() => { setShowNewTask(false); setReloadKey(k => k + 1); }}
          />
        </Suspense>
      )}

      {canExpenses && (
        <ExpenseFab
          openSheet={showExpenseFab}
          onSheetClose={() => setShowExpenseFab(false)}
          allowPropertyPick
        />
      )}

      {showAwaiting && (
        <Suspense fallback={null}>
          <IncomeListModal awaiting onClose={() => setShowAwaiting(false)} />
        </Suspense>
      )}

      {showAmcOverdue && (
        <AmcOverdueModal
          contracts={data?.signals?.amcOverduePayments?.contracts || []}
          onClose={() => setShowAmcOverdue(false)}
          navigate={navigate}
          t={t}
        />
      )}
    </div>
  );
}

// ── Модал AMC с просроченными платежами ────────────────────────────────────────
function AmcOverdueModal({ contracts, onClose, navigate, t }) {
  return (
    <div
      onClick={onClose}
      data-ph-id="amc-overdue-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)', borderRadius: '16px 16px 0 0',
          width: '100%', maxWidth: 600, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
        }}
      >
        {/* Хэндл + заголовок */}
        <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 14px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>
              {t('advisor.amc.modalTitle')}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, lineHeight: 1,
              }}
            >×</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
            {t('advisor.amc.modalSub')}
          </div>
        </div>

        {/* Список контрактов */}
        <div style={{ overflowY: 'auto', padding: '8px 16px', flex: 1, paddingBottom: 'max(32px, env(safe-area-inset-bottom))' }}>
          {contracts.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px 0', fontSize: 14 }}>
              —
            </div>
          ) : contracts.map(c => (
            <div
              key={c.id}
              data-ph-id="amc-overdue-contract-row"
              onClick={() => {
                onClose();
                navigate(`/properties/${c.property_id}`, {
                  state: { scrollToAmc: c.id },
                });
              }}
              style={{
                background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
                marginBottom: 8, padding: '12px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>📋</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.title}
                </div>
                {c.property_name && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                    📍 {c.property_name}
                  </div>
                )}
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)', flexShrink: 0 }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
